/**
 * Source code for this component adapted from mui.com demo
 * https://mui.com/material-ui/react-autocomplete/#google-maps-place
 */

import { useMemo, useState } from 'react'
import usePlacesService from 'react-google-autocomplete/lib/usePlacesAutocompleteService'
import parse from 'autosuggest-highlight/parse'

import {
  Autocomplete as MuiAutocomplete,
  Box,
  Grid,
  IconButton,
  styled,
  TextField,
  Typography
} from '@mui/material'

import {
  Close as CloseIcon,
  LocationOn as LocationOnIcon,
  Search as SearchIcon
} from '@mui/icons-material'

import config from '../../../config'

const Autocomplete = styled(MuiAutocomplete)(({ theme }) => ({
  '& .MuiOutlinedInput-root.MuiInputBase-sizeSmall': {
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.secondary.contrastText,
    padding: 0,
    zIndex: 1400,
  },
}))

const ButtonContainer = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.secondary.main,
  padding: 0,
  borderTopRightRadius: 5,
  borderBottomRightRadius: 5,
  cursor: 'pointer',
  height: 30,
}))

const Button = styled(IconButton)(({ theme }) => ({
  backgroundColor: theme.palette.secondary.main,
  color: theme.palette.common.white,
  height: 30,
  width: 30,
}))

function GoogleMapsSearch({
  autocompleteStyle,
  mapRef,
  onSelectPlace,
}) {
  const [inputValue, setInputValue] = useState('')
  const [value, setValue] = useState(null)

  const { placesService, getPlacePredictions, placePredictions } =
    usePlacesService({
      apiKey: config.googleMapsApiKey,
      options: {
        region: 'nz',
        componentRestrictions: { country: 'nz' },
      },
    })

  const handleChange = (_, newValue) => {
    setValue(newValue)

    if (newValue) {
      placesService?.getDetails(
        { placeId: newValue.place_id },
        (placeDetails) => onSelectPlace(placeDetails)
      )
    }
  }

  const handleInputChange = (_, newInputValue) => {
    const bounds = mapRef?.current?.getBounds()
    const boundsLiteral = bounds
      ? {
        east: bounds._northEast.lng,
        north: bounds._northEast.lat,
        south: bounds._southWest.lat,
        west: bounds._southWest.lng,
      }
      : {}
    getPlacePredictions({
      input: newInputValue,
      locationBias: boundsLiteral,
    })
    setInputValue(newInputValue)
  }

  const options = useMemo(
    () => (value ? [value, ...placePredictions] : placePredictions),
    [value, placePredictions]
  )

  return (
    <Autocomplete
      getOptionLabel={(option) =>
        typeof option === 'string' ? option : option.description
      }
      filterOptions={(x) => x}
      options={options}
      autoComplete
      includeInputInList
      filterSelectedOptions
      freeSolo
      value={value}
      inputValue={inputValue}
      onChange={handleChange}
      onInputChange={handleInputChange}
      sx={autocompleteStyle}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder="Search location..."
          size="small"
          fullWidth
          InputProps={{
            ...params.InputProps,
            style: {
              paddingLeft: 4,
              fontSize: '0.8rem',
              zIndex: 10,
            },
            endAdornment: (
              <ButtonContainer>
                <Button onClick={() => setInputValue('')}>
                  {inputValue ? <CloseIcon /> : <SearchIcon />}
                </Button>
              </ButtonContainer>
            ),
          }}
        />
      )}
      renderOption={(props, option) => {
        const matches =
          option.structured_formatting.main_text_matched_substrings
        const parts = parse(
          option.structured_formatting.main_text,
          matches.map((match) => [match.offset, match.offset + match.length])
        )

        return (
          <li {...props}>
            <Grid container alignItems="center">
              <Grid item>
                <Box
                  component={LocationOnIcon}
                  sx={{ color: 'text.secondary', mr: 2 }}
                />
              </Grid>
              <Grid item xs>
                {parts.map((part, index) => (
                  <span
                    key={index}
                    style={{
                      fontWeight: part.highlight ? 700 : 400,
                      fontSize: '0.9rem',
                    }}
                  >
                    {part.text}
                  </span>
                ))}

                <Typography
                  variant="body2"
                  sx={{ fontSize: '0.8rem' }}
                  color="text.primary"
                >
                  {option.structured_formatting.secondary_text}
                </Typography>
              </Grid>
            </Grid>
          </li>
        )
      }}
    />
  )
}

export default GoogleMapsSearch
